import { Tooltip } from 'antd';
import React from 'react';

import { Pair } from '../../../types/pair';
import { useSettingsStore } from '../../Settings/hooks/useSettingsStore';
import { formatCompactNumber, formatFollowers, formatTimeAgoCompact, formatTwitterHandle } from '../utils/formatters';
import TwitterIcon from './TwitterIcon';

type RelatedPairCardProps = {
  pair: Pair;
  index: number;
  highlighted?: boolean;
  onOpenTerminal?: (pair: Pair) => void;
  mainPairTwitterUsername?: string;
  mainPairDevAddress?: string;
};

const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

const RelatedPairCard: React.FC<RelatedPairCardProps> = ({
  pair,
  index,
  highlighted = false,
  onOpenTerminal,
  mainPairTwitterUsername,
  mainPairDevAddress,
}) => {
  const { general } = useSettingsStore();

  const getMetricClassName = (percentValue: number | null | undefined, threshold: number): string => {
    if (percentValue === undefined || percentValue === null) {
      return 'metric-neutral';
    }
    const isNegative = percentValue < 0;
    const isHighlighted = percentValue >= threshold;
    const shouldHighlightNegative = general.negativeHighlight && isNegative;
    return shouldHighlightNegative ? 'metric-negative' : (isHighlighted ? 'metric-highlight' : 'metric-neutral');
  };

  const bValues = [
    pair.b1,
    pair.b2,
    pair.b3,
    pair.b4,
    pair.b5,
    pair.b6,
    pair.b7,
    pair.b8,
    pair.b9,
    pair.b10,
  ];

  const hasAnyData = bValues.some(b => b !== undefined && b !== null);

  // Determine how this pair was matched
  const getMatchText = (): string | null => {
    const matchesTwitter = mainPairTwitterUsername && pair.twitterUsername === mainPairTwitterUsername;
    const matchesDev = mainPairDevAddress && pair.devAddress === mainPairDevAddress;

    if (matchesTwitter && matchesDev) {
      return 'matched by twitter username, dev address';
    } else if (matchesTwitter) {
      return 'matched by twitter user name';
    } else if (matchesDev) {
      return 'matched by dev address';
    }
    return 'not matched';
  };

  const matchText = getMatchText();

  const renderTwitterLine = () => {
    if (!pair.twitterUsername) {
      return null;
    }

    const twitterHandle = formatTwitterHandle(pair.twitterUsername);
    const hasFollowers = pair.twitterFollowers !== undefined && pair.twitterFollowers !== null;

    return (
      <div className="detail-line detail-line--twitter">
        <span className="detail-label detail-label--twitter">
          <TwitterIcon />
          Twitter:
        </span>
        <span className="detail-value">
          {pair.twitter ? (
            <a className="twitter-link" href={pair.twitter} target="_blank" rel="noopener noreferrer">
              {twitterHandle}
            </a>
          ) : (
            twitterHandle
          )}
          {hasFollowers && (
            <span className="twitter-followers">
              {' '}
              ({formatFollowers(pair.twitterFollowers)})
            </span>
          )}
        </span>
      </div>
    );
  };

  const canOpenTerminal = Boolean(onOpenTerminal && pair.tokenAddress && pair.pairAddress);

  if (!hasAnyData) {
    return (
      <div
        className="token-item"
        style={{ cursor: 'pointer' }}
        onDoubleClick={() => {
          if (canOpenTerminal) {
            onOpenTerminal?.(pair);
          }
        }}
      >
        <div className="token-item-header">
          <div className="token-name">
            {index + 1}. {pair.tokenTicker || 'N/A'}
            {pair.createdAt && <span className="token-creation-time">{formatTimeAgoCompact(pair.createdAt)}</span>}
            {pair.protocol && (
              <>
                <span className="token-protocol">[{pair.protocol}]</span>
                {pair.tokenName && <span className="token-name-part"> - {pair.tokenName}</span>}
              </>
            )}
            {canOpenTerminal && (
              <button
                type="button"
                className="token-open-terminal-link"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onOpenTerminal?.(pair);
                }}
              >
                Open terminal
              </button>
            )}
          </div>
          <div
            className="token-address"
            style={{ cursor: 'text' }}
            onDoubleClick={stopPropagation}
          >
            {pair.pairAddress || 'N/A'}
          </div>
        </div>
        {renderTwitterLine()}
        <div className="b-values">
          <span className="no-data-motion">no data</span>
        </div>
        {matchText && (
          <div className="related-pair-match-text">
            {matchText}
          </div>
        )}
      </div>
    );
  }

  const allZero = bValues.every(b => b === undefined || b === null || b === 0);

  const motionDisplay = (() => {
    if (pair.motion !== undefined && pair.motion !== null) {
      const percentPart =
        pair.motionPercent !== undefined && pair.motionPercent !== null
          ? ` (${pair.motionPercent > 0 ? '+' : ''}${pair.motionPercent}%)`
          : '';
      return `Motion: ${formatCompactNumber(pair.motion)}${percentPart}`;
    }
    if (pair.motionPercent !== undefined && pair.motionPercent !== null) {
      return `Motion: ${pair.motionPercent > 0 ? '+' : ''}${pair.motionPercent}%`;
    }
    return null;
  })();

  const bmotionDisplay = (() => {
    if (pair.bmotion !== undefined && pair.bmotion !== null) {
      const percentPart =
        pair.bmotionPercent !== undefined && pair.bmotionPercent !== null
          ? ` (${pair.bmotionPercent > 0 ? '+' : ''}${pair.bmotionPercent}%)`
          : '';
      return `BMotion: ${formatCompactNumber(pair.bmotion)}${percentPart}`;
    }
    if (pair.bmotionPercent !== undefined && pair.bmotionPercent !== null) {
      return `BMotion: ${pair.bmotionPercent > 0 ? '+' : ''}${pair.bmotionPercent}%`;
    }
    return null;
  })();

  const maxMcDisplay = (() => {
    if (pair.maxMC !== undefined && pair.maxMC !== null) {
      const percentPart =
        pair.maxMCPercent !== undefined && pair.maxMCPercent !== null
          ? ` (${pair.maxMCPercent > 0 ? '+' : ''}${pair.maxMCPercent}%)`
          : '';
      return `MaxMC: ${formatCompactNumber(pair.maxMC)}${percentPart}`;
    }
    return null;
  })();

  const feeDisplay = (() => {
    if (pair.totalPairFeesPaid !== undefined && pair.totalPairFeesPaid !== null) {
      return `Fee: ${formatCompactNumber(pair.totalPairFeesPaid)}`;
    }
    return null;
  })();

  const getFeeClassName = (): string => {
    if (pair.totalPairFeesPaid === undefined || pair.totalPairFeesPaid === null) {
      return 'metric-neutral';
    }
    const isHighlighted = pair.totalPairFeesPaid >= general.totalPairFeesHighlight;
    return isHighlighted ? 'metric-highlight' : 'metric-neutral';
  };

  const displayValues = [...bValues];
  while (
    displayValues.length > 0 &&
    (displayValues[displayValues.length - 1] === undefined ||
      displayValues[displayValues.length - 1] === null ||
      displayValues[displayValues.length - 1] === 0)
  ) {
    displayValues.pop();
  }

  return (
    <div
      className={`token-item${highlighted ? ' token-item--filter-match' : ''}`}
      style={{ cursor: 'pointer' }}
      onDoubleClick={() => {
        if (canOpenTerminal) {
          onOpenTerminal?.(pair);
        }
      }}
    >
      <div className="token-item-header">
        <div className="token-name">
          {index + 1}. {pair.tokenTicker || 'N/A'}
          {pair.createdAt && <span className="token-creation-time">{formatTimeAgoCompact(pair.createdAt)}</span>}
          {pair.protocol && (
            <>
              <span className="token-protocol">[{pair.protocol}]</span>
              {pair.tokenName && <span className="token-name-part"> - {pair.tokenName}</span>}
            </>
          )}
          {canOpenTerminal && (
            <button
              type="button"
              className="token-open-terminal-link"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenTerminal?.(pair);
              }}
            >
              Open terminal
            </button>
          )}
        </div>
        <div
          className="token-address"
          style={{ cursor: 'text' }}
          onDoubleClick={stopPropagation}
        >
          {pair.pairAddress || 'N/A'}
        </div>
      </div>

      <div className="token-metrics">
        {pair.openPrice !== undefined && pair.openPrice !== null && (
          <span className="metric metric-open-price">
            Open: {formatCompactNumber(Math.round(pair.openPrice * (pair.supply || 1)))}
          </span>
        )}
        {motionDisplay && (
          <span className={`metric ${getMetricClassName(pair.motionPercent, general.motionHighlight)}`}>
            {motionDisplay}
          </span>
        )}
        {bmotionDisplay && (
          <span className={`metric ${getMetricClassName(pair.bmotionPercent, general.bmotionHighlight)}`}>
            {bmotionDisplay}
          </span>
        )}
        {maxMcDisplay && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
            <span className={`metric ${getMetricClassName(pair.maxMCPercent, general.maxMCHighlight)}`}>
              {maxMcDisplay}
            </span>
            {feeDisplay && (
              <span className={`metric ${getFeeClassName()}`}>
                {feeDisplay}
              </span>
            )}
            {pair.showArrow && (
              <Tooltip title="BMotion > Motion" overlayClassName="arrow-tooltip">
                <span className="arrow-up">↑</span>
              </Tooltip>
            )}
            {pair.migrated && <span style={{ marginLeft: '2px' }}>M</span>}
          </div>
        )}
      </div>

      {renderTwitterLine()}

      {allZero ? (
        <div className="b-values">
          <span className="no-data-motion">no motion</span>
        </div>
      ) : (
        <div className="b-values">
          <span className="b-label">Candles:</span>
          {displayValues.map((bValue, bIndex) => {
            if (bValue === undefined || bValue === null) {
              return null;
            }
            const isPositive = bValue > 0;
            const isNegative = bValue < 0;
            return (
              <span
                key={bIndex}
                className={`b-value ${isPositive ? 'b-positive' : isNegative ? 'b-negative' : 'b-neutral'}`}
              >
                {isPositive ? '+' : ''}{bValue}
              </span>
            );
          })}
        </div>
      )}
      {matchText && (
        <div className="related-pair-match-text">
          {matchText}
        </div>
      )}
    </div>
  );
};

export default RelatedPairCard;
